import RouteTemplate from 'ember-route-template';

import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { get } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import LinksTo from 'ember-route-state/components/links-to.gjs';
import { pageTitle } from 'ember-page-title';

import CButton from 'rise-ui/components/c-button.gjs';
import SearchHelp from 'rise-ui/components/catalog/search/help.gjs';
import SearchFilters from 'rise-ui/components/catalog/search/filters.gjs';
import SearchResult from 'rise-ui/components/catalog/search/result.gjs';
import SearchTerm from 'rise-ui/components/catalog/search/term.gjs';
import DropdownPrompt from 'rise-ui/components/dropdown-prompt.gjs';
import ExpandingBlock from 'rise-ui/components/expanding-block.gjs';
import PageControls from 'rise-ui/components/page-controls.gjs';
import ReloadModel from 'rise-ui/components/reload-model.gjs';
import Select from 'rise-ui/components/select.gjs';
import Sticky from 'rise-ui/components/sticky.gjs';
import classMod from 'rise-ui/helpers/class-mod.js';
import event from 'rise-ui/helpers/event.js';
import ever from 'rise-ui/helpers/ever.js';
import nor from 'rise-ui/helpers/nor.js';
import or from 'rise-ui/helpers/or.js';
import set from 'rise-ui/helpers/set.js';
import sticky from 'rise-ui/modifiers/sticky.js';
import { stripEmpties } from 'rise-ui/utils/query-params.js';
import { or as orComputed, readOnly } from 'rise-ui/utils/object/computed.js';

export default RouteTemplate(class CatalogIndexTemplate extends Component {
  @service('catalog/search') catalogSearch;
  @service catalogSearches;
  @service router;
  @service ssr;

  orderDefault = 'relevance';
  @orComputed('{@controller.order,orderDefault}') orderValue;

  @tracked isStickyHelpShown = false;
  @tracked isHelpShown = false;

  @readOnly('catalogSearch.results') results;

  @action doSort(value) {
    this.router.transitionTo(
      this.router.currentRouteName,
      { queryParams: stripEmpties({
          order: value || undefined,
        }) }
    );
  }

  <template>
    {{pageTitle "Search for Data"}}

    {{#let (ever this.results.isResolved) as | resultsInit |}}
    <div class="id-catalog c-section">

      <Sticky @name="title">
        <h3 class="c-sticky-title">Catalog Search</h3>
      </Sticky>

      <h3 class="c-page-title qa-page-title"
        {{sticky activate="title"}}
      >
        Catalog Search
      </h3>

      <Sticky @name="help">
        <ExpandingBlock @button={{true}} @onToggle={{set this "isStickyHelpShown"}}>
          <:head>
            {{if this.isStickyHelpShown "Hide" "Show"}} Instructions
          </:head>
          <:body>
            <SearchHelp />
          </:body>
        </ExpandingBlock>
      </Sticky>

      <ExpandingBlock @button={{true}}
        @onToggle={{set this "isHelpShown"}}
        {{sticky activate="help"}}
      >
        <:head>
          {{if this.isHelpShown "Hide" "Show"}} Instructions
        </:head>
        <:body>
          <SearchHelp />
        </:body>
      </ExpandingBlock>

      <div class="c-actions">
        {{#if false}}
          <DropdownPrompt @side="bottom-end"
            @disabled={{this.isSearchUnfiltered}}
            @title={{if this.isSearchUnfiltered "Search is Unfiltered"}}
            @text="Save Search" @prompt="Name"
            @submitText={{if this.savedSearchOverwrite
              "Overwrite"
              (if this.savedSearch "Rename" "Save")
            }}
            @onResponse={{this.onSaveSearchResponse}}
            @onSubmit={{this.onSaveSearch}}
            @response={{this.savedSearch.name}}
            as | ddp |
          ><span
            {{didInsert this.setPrompt this ddp}}
          ></span></DropdownPrompt>
        {{/if}}
      </div>

      <div class="c-search">

        <div class="c-search__order">
          Order by:
          <Select class="c-search__order-control"
            onchange={{event this.doSort}}
            @selected={{this.orderValue}} as | select |>
            <select.Option @value="relevance">Relevance</select.Option>
            <select.Option @value="newest">Last Record Update (Newest)</select.Option>
            <select.Option @value="oldest">Last Record Update (Oldest)</select.Option>
            <select.Option @value="title">Alphabetical by Title</select.Option>
          </Select>
        </div>

        <div class="c-search__query">
          <h4 class="c-section-title c-search__query-label">Search</h4>
          <div class="c-search__query-reset">
            <CButton @activeDisabled={{true}}
              @activeTitle="Search is Clear"
              @linkTo={{get this.catalogSearch "linkToClear"}}
            >Clear Search/Filters</CButton>
          </div>

          <Sticky @name="search" @type="slim">
            <SearchTerm />
          </Sticky>
          <div class="c-search__query-term" {{sticky activate="search"}}>
            <SearchTerm />
          </div>

          <div class="c-search__query-hint">
            Search the catalog record title, description, tags, and generation effort
            and the item title and description.
            Operators are available to require inclusion (`+`), require exclusion (`-`),
            and require an exact phrase to appear in the search results (quotation marks).
            Boolean operators AND, OR, and NOT are also available.
            <LinksTo @route="help.page" @model="017b17bc-a8ab-47db-85d5-aa9ab79ec476">Details</LinksTo>
          </div>
        </div>

        <Sticky @name="filters--medium" @type="medium">
          <div class="c-search__filters">
            <SearchFilters />
          </div>
        </Sticky>
        <div class="c-search__filters" {{sticky activate="filters--medium"}}>
          <SearchFilters />
        </div>

        {{#if this.results.isPending}}
          <div class="c-search__meta c-search__meta--loading qa-search-loading">Loading Catalog...</div>
        {{else if resultsInit}}
          <div class="c-search__meta c-search__meta--count">
            {{or this.results.value.meta.totalItems "0"}} records found
          </div>
        {{/if}}

        <div class="c-search__results qa-search-results">
          <h4 class="c-section-title c-search__results-label">Results</h4>

          <div class="c-search__paging--before">
            <PageControls @config={{this.catalogSearch.paging}} />
          </div>

          <div class={{classMod "c-search__results"
            resultsInit "--init"
            (nor resultsInit this.results.value.length) "--empty"
          }}>
            {{#if this.results.isRejected}}
              <div class="c-no-result c-search__results-error qa-search-error">
                Error fetching records. <ReloadModel>Retry</ReloadModel>
              </div>
            {{else}}
              {{#each this.results.value as | record |}}
                <SearchResult @record={{record}} />
              {{else}}
                {{#if (or this.results.isPending this.ssr.isSsr)}}
                  <div class="c-no-result c-search__results-init qa-search-loading">Loading Catalog...</div>
                {{else}}
                  <div class="c-no-result c-search__results-empty qa-search-empty">No results match search and/or filter criteria</div>
                {{/if}}
              {{/each}}
            {{/if}}
          </div>

          <div class="c-search__paging--after">
            <PageControls @config={{this.catalogSearch.paging}} />
          </div>
        </div>
      </div>
    </div>
    {{/let}}
    {{outlet}}
  </template>
});
